"use client";

import { c as _c } from "react/compiler-runtime";
import { motion } from "framer-motion";
import { Activity, Check, Headphones, TrendingUp } from "lucide-react";
import type { HomeWellnessFeatureVisualAnxietyContent, HomeWellnessFeatureVisualAudioContent, HomeWellnessFeatureVisualMoodContent, HomeWellnessFeaturesSectionContent } from "@/features/home/types/home-page-content";
interface HomeWellnessFeaturesSectionProps {
  content: HomeWellnessFeaturesSectionContent;
}
function MoodTrackingVisual(t0) {
  const $ = _c(22);
  const {
    content
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-emerald-600/40 via-teal-900 to-slate-900" />;
    t2 = <div className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-emerald-400/20 mix-blend-screen blur-3xl transition-transform duration-1000 group-hover:scale-110" />;
    t3 = <div className="absolute right-1/4 bottom-1/4 h-96 w-96 rounded-full bg-amber-400/10 mix-blend-screen blur-3xl" />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      opacity: 0,
      y: 20,
      scale: 0.95
    };
    t5 = {
      opacity: 1,
      y: 0,
      scale: 1
    };
    t6 = {
      once: true,
      margin: "-100px"
    };
    t7 = {
      duration: 0.8,
      ease: "easeOut"
    };
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
    t7 = $[6];
  }
  let t8;
  if ($[7] !== content.title) {
    t8 = <h4 className="text-lg leading-tight font-medium text-white">{content.title}</h4>;
    $[7] = content.title;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== content.subtitle) {
    t9 = <p className="mt-1 text-xs text-white/60">{content.subtitle}</p>;
    $[9] = content.subtitle;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t8 || $[12] !== t9) {
    t10 = <div className="relative z-10">{t8}{t9}</div>;
    $[11] = t8;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="relative mt-6 flex h-32 w-full items-end"><svg className="absolute inset-0 h-full w-full overflow-visible" preserveAspectRatio="none" viewBox="0 0 100 100"><motion.path initial={{
          pathLength: 0
        }} whileInView={{
          pathLength: 1
        }} viewport={{
          once: true,
          margin: "-100px"
        }} transition={{
          duration: 1.5,
          ease: "easeInOut",
          delay: 0.2
        }} d="M0,80 Q15,70 25,75 T50,55 T75,20 T100,10" fill="none" stroke="#7DE2F8" strokeWidth="2.5" strokeLinecap="round" />{[{
          cx: 25,
          cy: 75
        }, {
          cx: 50,
          cy: 55
        }, {
          cx: 75,
          cy: 20
        }].map(_temp)}</svg></div>;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== content.intervals) {
    t12 = content.intervals.map(_temp2);
    $[15] = content.intervals;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t12) {
    t13 = <div className="relative z-10 mt-4 flex justify-between gap-1.5 sm:gap-2">{t12}</div>;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t10 || $[20] !== t13) {
    t14 = <div className="group relative aspect-[4/3] w-full overflow-hidden rounded-[2rem] bg-slate-900 sm:aspect-video">{t1}{t2}{t3}<motion.div initial={t4} whileInView={t5} viewport={t6} transition={t7} className="absolute top-1/2 left-1/2 flex w-[280px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-[2rem] border border-white/20 bg-white/10 p-6 shadow-2xl backdrop-blur-xl sm:w-[320px]">{t10}{t11}{t13}</motion.div></div>;
    $[19] = t10;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  return t14;
}
function _temp2(interval, index_0) {
  return <motion.div key={interval} initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.5 + index_0 * 0.1,
    duration: 0.4
  }} className={`rounded-full px-2.5 py-1.5 text-[9px] font-medium sm:text-[10px] ${index_0 === 0 ? "bg-[#7DE2F8] text-slate-900" : "bg-white/10 text-white transition-colors hover:bg-white/20"}`}>{interval}</motion.div>;
}
function _temp(dot, index) {
  return <motion.g key={index} initial={{
    opacity: 0,
    scale: 0
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true
  }} transition={{
    delay: 1 + index * 0.2,
    duration: 0.4
  }}><circle cx={dot.cx} cy={dot.cy} r="4" fill="#7DE2F8" /><circle cx={dot.cx} cy={dot.cy} r="8" fill="#7DE2F8" fillOpacity="0.2" /><line x1={dot.cx} y1={dot.cy} x2={dot.cx} y2="100" stroke="#7DE2F8" strokeWidth="1" strokeDasharray="3 3" className="opacity-40" /></motion.g>;
}
function AnxietySupportVisual(t0) {
  const $ = _c(26);
  const {
    content
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-slate-600 via-[#40523e] to-slate-900" />;
    t2 = <div className="absolute -top-1/4 -right-1/4 h-96 w-96 rounded-full bg-orange-200/10 mix-blend-screen blur-3xl" />;
    t3 = <div className="absolute bottom-0 left-1/4 h-64 w-[30rem] rounded-t-full bg-[#7DE2F8]/5 blur-3xl" />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      opacity: 0,
      y: 20,
      scale: 0.95
    };
    t5 = {
      opacity: 1,
      y: 0,
      scale: 1
    };
    t6 = {
      once: true,
      margin: "-100px"
    };
    t7 = {
      duration: 0.8,
      ease: "easeOut"
    };
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
    t7 = $[6];
  }
  let t10;
  let t11;
  let t8;
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      opacity: 0,
      x: -10
    };
    t9 = {
      opacity: 1,
      x: 0
    };
    t10 = {
      once: true
    };
    t11 = {
      delay: 0.3
    };
    $[7] = t10;
    $[8] = t11;
    $[9] = t8;
    $[10] = t9;
  } else {
    t10 = $[7];
    t11 = $[8];
    t8 = $[9];
    t9 = $[10];
  }
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <TrendingUp className="h-3.5 w-3.5" />;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== content.badge) {
    t13 = <motion.div initial={t8} whileInView={t9} viewport={t10} transition={t11} className="inline-flex items-center gap-1.5 self-start rounded-full border border-white/10 bg-white/20 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-md">{t12} {content.badge}</motion.div>;
    $[12] = content.badge;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <motion.polygon initial={{
      opacity: 0,
      scaleY: 0
    }} whileInView={{
      opacity: 1,
      scaleY: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 1,
      ease: "easeOut",
      delay: 0.4
    }} className="origin-bottom" points="0,100 0,80 15,75 30,85 45,60 65,55 85,30 100,20 100,100" fill="#7DE2F8" />;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="absolute right-0 bottom-16 left-0 h-24 opacity-60"><svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none" viewBox="0 0 100 100">{t14}<motion.polyline initial={{
          pathLength: 0
        }} whileInView={{
          pathLength: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1.5,
          ease: "easeOut",
          delay: 0.6
        }} points="0,80 15,75 30,85 45,60 65,55 85,30 100,20" fill="none" stroke="#bded45" strokeWidth="2" /></svg></div>;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <motion.div initial={{
      rotate: -90,
      opacity: 0
    }} whileInView={{
      rotate: 0,
      opacity: 1
    }} viewport={{
      once: true
    }} transition={{
      delay: 0.8,
      type: "spring"
    }}><Activity className="h-5 w-5 text-[#7DE2F8]" /></motion.div>;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  let t17;
  let t18;
  let t19;
  let t20;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      opacity: 0
    };
    t18 = {
      opacity: 1
    };
    t19 = {
      once: true
    };
    t20 = {
      delay: 1
    };
    $[17] = t17;
    $[18] = t18;
    $[19] = t19;
    $[20] = t20;
  } else {
    t17 = $[17];
    t18 = $[18];
    t19 = $[19];
    t20 = $[20];
  }
  let t21;
  if ($[21] !== content.progress) {
    t21 = <div className="relative z-10 mt-auto flex items-center gap-2 text-white">{t16}<motion.span initial={t17} whileInView={t18} viewport={t19} transition={t20} className="text-[15px] font-medium">{content.progress}</motion.span></div>;
    $[21] = content.progress;
    $[22] = t21;
  } else {
    t21 = $[22];
  }
  let t22;
  if ($[23] !== t13 || $[24] !== t21) {
    t22 = <div className="group relative aspect-[4/3] w-full overflow-hidden rounded-[2rem] bg-slate-900 sm:aspect-video">{t1}{t2}{t3}<motion.div initial={t4} whileInView={t5} viewport={t6} transition={t7} className="absolute top-1/2 left-1/2 flex h-[200px] w-[280px] -translate-x-1/2 -translate-y-1/2 flex-col justify-between overflow-hidden rounded-[2rem] border border-white/20 bg-white/10 p-6 shadow-2xl backdrop-blur-xl sm:w-[320px]">{t13}{t15}{t21}</motion.div></div>;
    $[23] = t13;
    $[24] = t21;
    $[25] = t22;
  } else {
    t22 = $[25];
  }
  return t22;
}
function AudioContentVisual(t0) {
  const $ = _c(17);
  const {
    content
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-indigo-900/80 via-slate-800 to-slate-950" />;
    t2 = <div className="absolute top-1/2 left-1/2 h-[30rem] w-[30rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-500/10 mix-blend-screen blur-3xl motion-safe:animate-pulse" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0,
      y: 20,
      scale: 0.95
    };
    t4 = {
      opacity: 1,
      y: 0,
      scale: 1
    };
    t5 = {
      once: true,
      margin: "-100px"
    };
    t6 = {
      duration: 0.8,
      ease: "easeOut"
    };
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/20 backdrop-blur-sm"><Headphones className="h-6 w-6 text-white" /></div>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== content.title) {
    t8 = <h4 className="text-lg leading-tight font-medium text-white">{content.title}</h4>;
    $[7] = content.title;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== content.subtitle) {
    t9 = <p className="mt-0.5 text-xs text-white/70">{content.subtitle}</p>;
    $[9] = content.subtitle;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t8 || $[12] !== t9) {
    t10 = <div className="relative z-10 mb-8 flex items-center gap-4">{t7}<div>{t8}{t9}</div></div>;
    $[11] = t8;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="relative z-10 flex h-16 items-center justify-center gap-1.5">{[30, 50, 40, 80, 50, 100, 70, 40, 60, 30].map(_temp3)}</div>;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t10) {
    t12 = <div className="group relative aspect-[4/3] w-full overflow-hidden rounded-[2rem] bg-slate-900 sm:aspect-video">{t1}{t2}<motion.div initial={t3} whileInView={t4} viewport={t5} transition={t6} className="absolute top-1/2 left-1/2 w-[280px] -translate-x-1/2 -translate-y-1/2 rounded-[2rem] border border-white/20 bg-white/10 p-6 shadow-2xl backdrop-blur-xl sm:w-[320px] sm:p-8">{t10}{t11}</motion.div></div>;
    $[15] = t10;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  return t12;
}
function _temp3(height, index) {
  return <motion.div key={index} className="w-2 rounded-full bg-[#D1F570]" animate={{
    height: [`${height * 0.5}%`, `${height}%`, `${height * 0.5}%`]
  }} transition={{
    delay: index * 0.1,
    duration: 1.5,
    ease: "easeInOut",
    repeat: Infinity
  }} />;
}
export function HomeWellnessFeaturesSection(t0) {
  const $ = _c(29);
  const {
    content
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      y: 20
    };
    t2 = {
      opacity: 1,
      y: 0
    };
    t3 = {
      once: true,
      margin: "-100px"
    };
    t4 = {
      duration: 0.7,
      ease: "easeOut"
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="flex h-1.5 w-1.5 rounded-full bg-slate-400" />;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== content.eyebrow) {
    t6 = <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-1.5 shadow-sm">{t5}<span className="text-xs font-medium text-slate-600">{content.eyebrow}</span></div>;
    $[5] = content.eyebrow;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== content.title) {
    t7 = <h2 className="text-4xl leading-[1.1] font-semibold tracking-tight text-balance text-foreground sm:text-5xl lg:text-5xl">{content.title}</h2>;
    $[7] = content.title;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== content.description) {
    t8 = <p className="mt-6 hidden max-w-md text-lg leading-relaxed text-slate-600 sm:block">{content.description}</p>;
    $[9] = content.description;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t6 || $[12] !== t7 || $[13] !== t8) {
    t9 = <div className="static z-20 h-fit lg:sticky lg:top-40 lg:w-[45%]"><motion.div initial={t1} whileInView={t2} viewport={t3} transition={t4}>{t6}{t7}{t8}</motion.div></div>;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== content.anxietyVisual || $[16] !== content.audioVisual || $[17] !== content.features || $[18] !== content.moodVisual) {
    let t11;
    if ($[20] !== content.anxietyVisual || $[21] !== content.audioVisual || $[22] !== content.moodVisual) {
      t11 = feature => <div key={feature.id} className="group relative flex flex-col gap-8">{feature.id === "mood" ? <MoodTrackingVisual content={content.moodVisual} /> : null}{feature.id === "anxiety" ? <AnxietySupportVisual content={content.anxietyVisual} /> : null}{feature.id === "audio" ? <AudioContentVisual content={content.audioVisual} /> : null}<motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.6
        }} className="flex max-w-xl flex-col"><h3 className="text-3xl font-medium tracking-tight text-foreground">{feature.title}</h3><p className="mt-3 text-[17px] leading-relaxed text-slate-600">{feature.description}</p><ul className="mt-8 space-y-4">{feature.benefits.map(_temp4)}</ul></motion.div></div>;
      $[20] = content.anxietyVisual;
      $[21] = content.audioVisual;
      $[22] = content.moodVisual;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    t10 = content.features.map(t11);
    $[15] = content.anxietyVisual;
    $[16] = content.audioVisual;
    $[17] = content.features;
    $[18] = content.moodVisual;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[24] !== t10) {
    t11 = <div className="z-10 flex flex-col gap-24 pt-8 lg:w-[55%] lg:pt-0">{t10}</div>;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t9) {
    t12 = <section className="relative w-full bg-white py-24 sm:py-32"><div className="mx-auto max-w-7xl px-6 lg:px-8"><div className="relative flex flex-col gap-16 lg:flex-row lg:gap-24">{t9}{t11}</div></div></section>;
    $[26] = t11;
    $[27] = t9;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
}
function _temp4(benefit, index) {
  return <motion.li key={benefit} className="flex items-center gap-3 text-slate-700" initial={{
    opacity: 0,
    x: -10
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.2 + index * 0.1
  }}><div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-secondary/20"><Check className="h-3.5 w-3.5 text-secondary" strokeWidth={2.5} /></div><span className="text-[15px] font-medium">{benefit}</span></motion.li>;
}